// Relógio do servidor visto do navegador.
//
// O estado de cada equipamento é decidido por diferenças de poucas dezenas de segundos
// (COMMS_TIMEOUT_S, TELEMETRY_TIMEOUT_S) entre "agora" e o last_seen que o SERVIDOR
// carimbou. Se o "agora" for o relógio da estação do operador, o limiar fica na ordem
// da deriva de qualquer máquina que não corre NTP: uma estação atrasada pinta a frota
// inteira de "sem comunicação" enquanto os equipamentos transmitem normalmente, e uma
// adiantada nunca mostra uma queda de link. Não há nada na tela que aponte para o relógio, então o operador
// conclui que a mina parou.
//
// O servidor passou a carimbar `server_time` em todo frame do WebSocket e em
// /api/config. Guardamos o DESVIO entre os dois relógios e derivamos o "agora" dele.
// Desvio, e não uma cópia do instante: uma cópia envelheceria entre dois frames.

let skewMs = 0
let synced = false

// syncServerClock recebe o instante do servidor (RFC 3339) e recalcula o desvio.
// Ignora valor ilegível: melhor seguir com o desvio anterior do que adotar NaN, que
// contaminaria toda comparação de tempo da interface.
export function syncServerClock(iso) {
  if (!iso) return
  const server = Date.parse(iso)
  if (!Number.isFinite(server)) return
  skewMs = server - Date.now()
  synced = true
}

// serverNow é o "agora" do servidor, em ms. Use isto em toda comparação com um
// instante que veio do servidor; Date.now() cru só serve para medir duração local.
export function serverNow() {
  return Date.now() + skewMs
}

// clockSkewMs expõe o desvio para diagnóstico (a tela de Debug mostra).
export function clockSkewMs() {
  return synced ? skewMs : 0
}

export function clockSynced() {
  return synced
}

// ---- Dia no fuso da mina ------------------------------------------------------------
//
// A API resolve toda janela analítica no fuso da MINA em escopo e devolve qual usou em
// `window.timezone`. A interface cortava o dia no fuso do NAVEGADOR, então um telão
// aberto num fuso diferente do da mina virava o dia no meio do turno, e um intervalo
// escolhido no seletor pedia um pedaço de dois dias da mina.

// zoneOffsetMs devolve o deslocamento do fuso, em ms, no instante dado. Sai do próprio
// Intl: é a única fonte de fuso que o navegador tem, e resolve horário de verão sem
// tabela nenhuma do nosso lado.
function zoneOffsetMs(tz, at) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const p = {}
  for (const part of dtf.formatToParts(at)) p[part.type] = part.value
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second)
  // Compara segundo com segundo: o formatToParts não devolve milissegundos, e sem
  // descontá-los do outro lado o deslocamento sai com até 999 ms de erro, que era o
  // bastante para o fim do dia cair um segundo dentro do dia seguinte.
  return asUTC - (at.getTime() - at.getMilliseconds())
}

// dayInZone é a data (YYYY-MM-DD) daquele instante no fuso, ou no fuso do navegador
// quando não há um cadastrado.
export function dayInZone(tz, at = new Date(serverNow())) {
  if (!tz) return new Intl.DateTimeFormat('en-CA').format(at)
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(at)
  } catch {
    return new Intl.DateTimeFormat('en-CA').format(at)
  }
}

// zonedDayBounds devolve o instante ISO do começo e do fim do dia NAQUELE fuso. Duas
// passagens porque o deslocamento depende do instante, e um dia de virada de horário de
// verão muda de deslocamento dentro de si mesmo.
export function zonedDayBounds(day, tz) {
  const naiveStart = Date.parse(`${day}T00:00:00.000Z`)
  const naiveEnd = Date.parse(`${day}T23:59:59.999Z`)
  if (!Number.isFinite(naiveStart)) return { from: null, to: null }
  if (!tz) {
    return {
      from: new Date(`${day}T00:00:00.000`).toISOString(),
      to: new Date(`${day}T23:59:59.999`).toISOString(),
    }
  }
  const resolve = (naive) => {
    let ms = naive - zoneOffsetMs(tz, new Date(naive))
    ms = naive - zoneOffsetMs(tz, new Date(ms))
    return new Date(ms).toISOString()
  }
  try {
    return { from: resolve(naiveStart), to: resolve(naiveEnd) }
  } catch {
    return {
      from: new Date(`${day}T00:00:00.000`).toISOString(),
      to: new Date(`${day}T23:59:59.999`).toISOString(),
    }
  }
}
